import { generateMnemonic, validateMnemonic } from '@scure/bip39';
import { wordlist } from '@scure/bip39/wordlists/english.js';
import { PASSPHRASE_ENTROPY_BITS, PASSPHRASE_WORD_COUNT } from './constants';
import { SyncError } from './errors';

/** Why a passphrase was rejected. */
export const PassphraseIssue = {
  /** The passphrase does not consist of exactly 12 words. */
  WordCount: 'wordCount',
  /** A word is not in the BIP39 English word list; `wordIndex` points at the first one. */
  UnknownWord: 'unknownWord',
  /** All words are valid but the built-in checksum fails: a word is mistyped or in the wrong place. */
  Checksum: 'checksum',
} as const;
export type PassphraseIssue = (typeof PassphraseIssue)[keyof typeof PassphraseIssue];

export type PassphraseValidation =
  | { readonly valid: true; readonly normalized: string }
  | {
      readonly valid: false;
      readonly reason: PassphraseIssue;
      /** Zero-based position of the first unknown word (only for `UnknownWord`). */
      readonly wordIndex?: number;
    };

const knownWords: ReadonlySet<string> = new Set(wordlist);

/**
 * The canonical text of a passphrase, which key derivation hashes: Unicode NFKD, lower case,
 * words separated by one space, no leading or trailing space.
 */
export function normalizePassphrase(input: string): string {
  return input
    .normalize('NFKD')
    .toLowerCase()
    .split(/\s+/u)
    .filter((word) => word.length > 0)
    .join(' ');
}

/** A new random passphrase: 12 BIP39 English words (128 bits of entropy). */
export function generatePassphrase(): string {
  return generateMnemonic(wordlist, PASSPHRASE_ENTROPY_BITS);
}

/** Check a passphrase typed by the user; the result carries the normalised text when valid. */
export function validatePassphrase(input: string): PassphraseValidation {
  const normalized = normalizePassphrase(input);
  const words = normalized === '' ? [] : normalized.split(' ');
  if (words.length !== PASSPHRASE_WORD_COUNT) return { valid: false, reason: PassphraseIssue.WordCount };
  const unknown = words.findIndex((word) => !knownWords.has(word));
  if (unknown >= 0) return { valid: false, reason: PassphraseIssue.UnknownWord, wordIndex: unknown };
  if (!validateMnemonic(normalized, wordlist)) return { valid: false, reason: PassphraseIssue.Checksum };
  return { valid: true, normalized };
}

export class InvalidPassphraseError extends SyncError {
  readonly reason: PassphraseIssue;
  readonly wordIndex: number | undefined;
  constructor(reason: PassphraseIssue, wordIndex?: number) {
    super(`The passphrase is invalid: ${reason}`);
    this.reason = reason;
    this.wordIndex = wordIndex;
  }
}

/** The normalised passphrase, or an `InvalidPassphraseError`. */
export function requireValidPassphrase(input: string): string {
  const result = validatePassphrase(input);
  if (!result.valid) throw new InvalidPassphraseError(result.reason, result.wordIndex);
  return result.normalized;
}
